/**
 * Widget D: Social Security Claiming Strategy Distribution
 * 
 * Displays a pie chart showing the distribution of clients by their
 * Social Security claiming strategy: Claim at 62, Claim at FRA, Claim at 70, or Unknown.
 */

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateSocialSecurityClaiming, SocialSecurityData } from "../utils/widgetCalculations";
import { UNIFIED_CHART_COLORS, ACCENT_COLOR } from "./widgetColors";
import { ChartTooltip, CHART_MARGINS, CHART_ANIMATION, renderPieLabel } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";
import { isDataEmpty, getSampleData } from "../utils/sampleData";
import { Badge } from "../../ui/badge";

interface SocialSecurityWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

const COLORS = {
  "62": UNIFIED_CHART_COLORS[1],      // Sky blue (index 1)
  FRA: UNIFIED_CHART_COLORS[6],       // Mint green (index 6)
  "70": UNIFIED_CHART_COLORS[2],      // Lavender (index 2)
  unknown: UNIFIED_CHART_COLORS[9],    // Blue-gray (index 9)
};

const CATEGORY_LABELS = {
  "62": "Claim at 62",
  FRA: "Claim at FRA",
  "70": "Claim at 70",
  unknown: "Unknown",
};

export function SocialSecurityWidget({
  clients,
  isLoading = false,
  userId,
}: SocialSecurityWidgetProps) {
  const api = XanoAPI.getInstance();

  const { data: apiData, isLoading: apiLoading } = useQuery({
    queryKey: ["social-security", userId],
    queryFn: async () => {
      const result = await api.getSocialSecurity(userId);
      console.log("🔍 SocialSecurityWidget: API response:", result);
      console.log("🔍 SocialSecurityWidget: Has categories?", !!result?.categories);
      return result;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const data = useMemo(() => {
    let result: {
      categories: {
        "62": number;
        FRA: number;
        "70": number;
        unknown: number;
      };
    };
    let usingSampleData = false;
    
    console.log("🔍 SocialSecurityWidget: Processing data - apiData:", apiData);
    console.log("🔍 SocialSecurityWidget: apiData is null?", apiData === null);
    console.log("🔍 SocialSecurityWidget: apiData is undefined?", apiData === undefined);
    console.log("🔍 SocialSecurityWidget: Clients available:", clients.length);
    
    // Use API data if it exists and has categories structure with non-zero values
    if (apiData && apiData.categories && typeof apiData.categories === 'object' && Object.keys(apiData.categories).length > 0) {
      const totalClients = Object.values(apiData.categories).reduce((sum: number, val: any) => sum + (typeof val === 'number' ? val : 0), 0);
      console.log("🔍 SocialSecurityWidget: API data found - Total clients:", totalClients);
      console.log("🔍 SocialSecurityWidget: Categories:", JSON.stringify(apiData.categories, null, 2));
      
      if (totalClients > 0) {
        // API data has values - use it
        console.log("✅ SocialSecurityWidget: Using API data");
        result = apiData;
        usingSampleData = false;
      } else {
        // API data exists but is empty - try calculating from clients
        console.log("⚠️ SocialSecurityWidget: API data is empty (all zeros), trying client calculation");
        if (clients.length > 0) {
          try {
            const calculatedData = calculateSocialSecurityClaiming(clients);
            const calculatedTotal = Object.values(calculatedData.categories).reduce((a, b) => a + b, 0);
            console.log("🔍 SocialSecurityWidget: Calculated from clients - Total:", calculatedTotal);
            if (calculatedTotal > 0) {
              console.log("✅ SocialSecurityWidget: Using calculated data from clients");
              result = calculatedData;
              usingSampleData = false;
            } else {
              console.log("⚠️ SocialSecurityWidget: Calculated data also empty, using sample data");
              result = getSampleData.socialSecurity();
              usingSampleData = true;
            }
          } catch (error) {
            console.error("❌ SocialSecurityWidget: Error calculating from clients:", error);
            result = getSampleData.socialSecurity();
            usingSampleData = true;
          }
        } else {
          console.log("⚠️ SocialSecurityWidget: No clients available, using sample data");
          result = getSampleData.socialSecurity();
          usingSampleData = true;
        }
      }
    } else {
      // No API data structure available - try calculating from clients first
      console.log("⚠️ SocialSecurityWidget: No API data structure available");
      console.log("🔍 SocialSecurityWidget: apiData:", apiData);
      
      if (clients.length > 0) {
        try {
          const calculatedData = calculateSocialSecurityClaiming(clients);
          const calculatedTotal = Object.values(calculatedData.categories).reduce((a, b) => a + b, 0);
          console.log("🔍 SocialSecurityWidget: Calculated from clients - Total:", calculatedTotal);
          if (calculatedTotal > 0) {
            console.log("✅ SocialSecurityWidget: Using calculated data from clients");
            result = calculatedData;
            usingSampleData = false;
          } else {
            console.log("⚠️ SocialSecurityWidget: Calculated data empty, using sample data");
            result = getSampleData.socialSecurity();
            usingSampleData = true;
          }
        } catch (error) {
          console.error("❌ SocialSecurityWidget: Error calculating from clients:", error);
          result = getSampleData.socialSecurity();
          usingSampleData = true;
        }
      } else {
        console.log("⚠️ SocialSecurityWidget: No clients available, using sample data");
        result = getSampleData.socialSecurity();
        usingSampleData = true;
      }
    }
    
    // Transform to chart data format
    const totalClients = Object.values(result.categories).reduce((a, b) => a + b, 0);
    console.log("🔍 SocialSecurityWidget: Total clients:", totalClients);
    console.log("🔍 SocialSecurityWidget: Category entries:", Object.entries(result.categories));
    
    const chartData = Object.entries(result.categories)
      .filter(([_, count]) => count > 0)
      .map(([key, count]) => ({
        name: CATEGORY_LABELS[key as keyof typeof CATEGORY_LABELS],
        value: count,
        percentage: totalClients > 0 ? (count / totalClients) * 100 : 0,
        fill: COLORS[key as keyof typeof COLORS],
        key,
      }));
    
    console.log("🔍 SocialSecurityWidget: Chart data after filtering:", chartData);
    console.log("🔍 SocialSecurityWidget: Chart data length:", chartData.length);

    return { chartData, totalClients, usingSampleData };
  }, [apiData, clients]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.totalClients === 0 || data.chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No Social Security claiming strategy data available.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {data.usingSampleData && (
          <div className="mb-2 flex justify-end">
            <Badge variant="secondary" className="text-xs">Sample Data</Badge>
          </div>
        )}
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={CHART_MARGINS}>
              <Pie
                data={data.chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => {
                  const percentage = entry.percentage !== undefined 
                    ? entry.percentage 
                    : (entry.payload?.percentage !== undefined ? entry.payload.percentage : 0);
                  if (percentage < 5) return "";
                  return (
                    <text
                      x={entry.x}
                      y={entry.y}
                      fill="hsl(var(--foreground))"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="13"
                      fontWeight="600"
                      style={{ 
                        textShadow: "0 1px 2px rgba(0,0,0,0.1), 0 0 4px rgba(255,255,255,0.8)",
                        filter: "drop-shadow(0 0 2px rgba(255,255,255,0.9))"
                      }}
                    >
                      {percentage.toFixed(0)}%
                    </text>
                  );
                }}
                outerRadius={95}
                innerRadius={60}
                paddingAngle={3}
                dataKey="value"
                {...CHART_ANIMATION}
              >
                {data.chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.fill}
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                    opacity={0.85}
                  />
                ))}
              </Pie>
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value) => {
                    const dataPoint = data.chartData.find(d => d.value === value);
                    return [
                      `${value} clients (${dataPoint?.percentage.toFixed(1)}%)`,
                      dataPoint?.name || ""
                    ];
                  }}
                />} 
                cursor={{ fill: "transparent" }}
              />
              <Legend
                verticalAlign="bottom"
                height={60}
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ paddingTop: "15px" }}
                formatter={(value, entry: any) => {
                  const dataPoint = data.chartData.find((d) => d.key === entry.payload?.key);
                  if (!dataPoint) return value;
                  return (
                    <span className="text-xs text-muted-foreground">
                      {value} <span className="font-semibold text-foreground">{dataPoint.value}</span> ({dataPoint.percentage.toFixed(1)}%)
                    </span>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t text-center">
          {data.chartData.map((item) => (
            <div key={item.key} className="space-y-1">
              <div className="text-lg font-bold" style={{ color: item.fill }}>
                {item.value}
              </div>
              <div className="text-xs font-medium text-foreground">{item.name}</div>
              <div className="text-xs text-muted-foreground">
                {item.percentage.toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

